import React, {useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import ProvidenceService from "../../../../services/dispatch/providence-service.js";
import {useRequiredFields} from "../../../../hooks/use-required-fields.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const initialProvidence = {name: ""};

const ProvidenceForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const [state, setState] = useState(initialProvidence);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);
    const nameReference = useRef(null);
    const {clearRequiredFieldError, getRequiredFieldProps, validateRequiredFields} = useRequiredFields();

    const {isLoading: isQueryLoading, refetch} = useQuery({
        queryFn: async () => {
            const data = await ProvidenceService.get(id).catch(() => null);
            if (!data) {
                navigate("/404");
                return;
            }

            const {providence} = data;
            setState({id: providence.id, name: providence.name ?? ""});
            return providence;
        },
        queryKey: ["dispatch-providence", id],
        enabled: !!id,
    });

    const onChange = ({target: {value, id}}) => {
        setState(prev => ({...prev, [id]: value}));
        clearRequiredFieldError(id, value);
    };

    const persist = (addAfter = false) => {
        if (!validateRequiredFields([{id: "name", value: state.name, reference: nameReference}])) return;

        const payload = {name: state.name};

        const setSaving = addAfter ? setIsSavingAndAdding : setIsSaving;
        setSaving(true);
        const response = id ? ProvidenceService.update(id, payload) : ProvidenceService.save(payload);
        response
            .then((data) => {
                if (addAfter) {
                    if (id) {
                        projectUtils.success(true);
                        navigate(API_DESPACHO_WEB + "/providence/form", false);
                    } else {
                        projectUtils.success(false);
                        setState(initialProvidence);
                    }
                    return;
                }

                projectUtils.success(true);

                if (id) {
                    navigate(API_DESPACHO_WEB + "/providence", false);
                    return;
                }

                navigate(`${API_DESPACHO_WEB}/providence/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const errMessage = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir a providência "${state.name}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                ProvidenceService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/providence", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const errMessage = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            });
    };

    const newProvidence = () => navigate(API_DESPACHO_WEB + "/providence/form", false);

    return (
        <Template isLoading={isSaving || isSavingAndAdding || isQueryLoading}>
            <Template.Header>
                <Template.Title>Providência</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newProvidence}><FaPlus/>Adicionar providência</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <InputForm classNameWrapper="col-span-12" inputClass={REQUIRED_BG} labelClass={REQUIRED_LABEl_BG} id="name" label="Nome" value={state.name} onChange={onChange} reference={nameReference} {...getRequiredFieldProps("name")}/>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        <Button target="primary-outline" onClick={() => persist(true)} loading={isSavingAndAdding} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist(false)} loading={isSaving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default ProvidenceForm;